import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Container,
  Grid,
  Divider,
  Collapse,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ShoppingCart,
  ExpandMore,
  ExpandLess,
  Add,
  Remove,
  Person,
  LocationOn,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { trackEvent } from "../../analytics";
import logger from "../../utils/logger";


const CheckoutPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [orderSummaryExpanded, setOrderSummaryExpanded] = useState(true);
  const [recipientExpanded, setRecipientExpanded] = useState(true);
  const [deliveryAddressExpanded, setDeliveryAddressExpanded] = useState(true);
  const [hasStartedRecipient, setHasStartedRecipient] = useState(false);
  const [hasStartedAddress, setHasStartedAddress] = useState(false);

  const [cartItems, setCartItems] = useState([]);
  const [upsells, setUpsells] = useState([]);

  // Customer details form
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  // Shipping address form
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [country, setCountry] = useState("India");

  const isCampaign = productId?.startsWith("campaign_");

  const totalAmount = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.amount * item.quantity, 0);
  }, [cartItems]);

  useEffect(() => {
    fetchProductDetails();
  }, [productId]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      
      let url;
      if (isCampaign) {
        url = `${process.env.REACT_APP_BACKEND_URL}/checkout/campaign/${productId}/details`;
      } else {
        url = `${process.env.REACT_APP_BACKEND_URL}/checkout/product-details/${productId}`;
      }

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Product not found");
      }

      const data = await response.json();
      
      // Normalize data for campaigns (originalPrice -> compareAtPrice, discountedPrice -> amount)
      const normalizedProduct = data.product;
      if (isCampaign) {
        normalizedProduct.amount = data.product.discountedPrice;
        normalizedProduct.compareAtPrice = data.product.originalPrice;
      }
      
      setProduct(normalizedProduct);
      setUpsells(data.upsellProducts || []);

      const initialVariants = {};
      if (normalizedProduct.variants && normalizedProduct.variants.length > 0) {
        const groups = {};
        normalizedProduct.variants.forEach(v => {
          if (!v.option) return;
          if (!groups[v.option]) groups[v.option] = [];
          if (!groups[v.option].includes(v.value)) groups[v.option].push(v.value);
        });
        Object.keys(groups).forEach(opt => {
          if (groups[opt].length > 0) {
            initialVariants[opt] = groups[opt][0];
          }
        });
      }

      setCartItems([
        {
          productId: productId,
          productName: normalizedProduct.productName || normalizedProduct.title || "",
          productSku: normalizedProduct.productSku || "",
          imageUrl: normalizedProduct.imageUrl || "",
          amount: normalizedProduct.amount,
          compareAtPrice: normalizedProduct.compareAtPrice,
          quantity: 1,
          selectedVariants: initialVariants,
          variants: normalizedProduct.variants || []
        }
      ]);
      
      // Track Checkout Started
      const trackingProps = {
        productId: productId,
        productName: normalizedProduct.productName,
        amount: normalizedProduct.amount,
        isCampaign: isCampaign
      };
      trackEvent("Checkout Started", trackingProps);
      logger.info("Checkout Started", trackingProps);
    } catch (err) {
      console.error("Error fetching product:", err);
      setError(err.message || "Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const getVariantGroups = (variants) => {
    if (!variants) return {};
    const groups = {};
    variants.forEach(v => {
      if (!v.option) return;
      if (!groups[v.option]) groups[v.option] = [];
      if (!groups[v.option].includes(v.value)) groups[v.option].push(v.value);
    });
    return groups;
  };

  const handleAddUpsell = (upsellProduct) => {
    const alreadyInCart = cartItems.some(item => item.productId === `prod_${upsellProduct._id}`);
    if (alreadyInCart) {
      toast.info("This item is already in your cart. You can increase its quantity in the order summary.");
      return;
    }

    const initialVariants = {};
    if (upsellProduct.variants && upsellProduct.variants.length > 0) {
      const groups = {};
      upsellProduct.variants.forEach(v => {
        if (!v.option) return;
        if (!groups[v.option]) groups[v.option] = [];
        if (!groups[v.option].includes(v.value)) groups[v.option].push(v.value);
      });
      Object.keys(groups).forEach(opt => {
        if (groups[opt].length > 0) {
          initialVariants[opt] = groups[opt][0];
        }
      });
    }

    const newCartItem = {
      productId: `prod_${upsellProduct._id}`,
      productName: upsellProduct.title,
      productSku: upsellProduct.sku || "",
      imageUrl: upsellProduct.thumbnailUrl || "",
      amount: Math.round(upsellProduct.price * 100),
      compareAtPrice: upsellProduct.compareAtPrice ? Math.round(upsellProduct.compareAtPrice * 100) : null,
      quantity: 1,
      selectedVariants: initialVariants,
      variants: upsellProduct.variants || []
    };

    setCartItems([...cartItems, newCartItem]);
    toast.success(`${upsellProduct.title} added to cart!`);
    trackEvent("Upsell Item Added", { productId: upsellProduct._id });
  };

  const handleUpdateQuantity = (itemId, newQty) => {
    if (newQty < 1) {
      if (itemId === productId) {
        return;
      }
      setCartItems(cartItems.filter(item => item.productId !== itemId));
      return;
    }
    setCartItems(cartItems.map(item => {
      if (item.productId === itemId) {
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const handleUpdateVariant = (itemId, optionName, value) => {
    setCartItems(cartItems.map(item => {
      if (item.productId === itemId) {
        const updatedVariants = { ...item.selectedVariants, [optionName]: value };
        return { ...item, selectedVariants: updatedVariants };
      }
      return item;
    }));
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    // Validation
    if (!customerName || !customerEmail || !customerPhone) {
      toast.error("Please fill in all customer details");
      return;
    }

    if (!addressLine1 || !city || !state || !zipcode) {
      toast.error("Please fill in all shipping address fields");
      return;
    }

    setProcessing(true);
    
    const paymentProps = {
      productId,
      amount: totalAmount,
      quantity: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      isCampaign
    };
    trackEvent("Payment Initiated", paymentProps);
    logger.info("Payment Initiated", paymentProps);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load Razorpay. Please try again.");
      }

      // Create Razorpay order
      let orderUrl;
      let orderBody;

      const formattedItems = cartItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
        selectedVariants: item.selectedVariants
      }));

      if (isCampaign) {
        orderUrl = `${process.env.REACT_APP_BACKEND_URL}/checkout/campaign/create-order`;
        orderBody = {
          campaignId: productId,
          quantity: cartItems[0]?.quantity || 1,
          selectedVariants: cartItems[0]?.selectedVariants || {},
          items: formattedItems
        };
      } else {
        orderUrl = `${process.env.REACT_APP_BACKEND_URL}/checkout/create-order`;
        orderBody = {
          productId: productId,
          quantity: cartItems[0]?.quantity || 1,
          selectedVariants: cartItems[0]?.selectedVariants || {},
          items: formattedItems
        };
      }

      const orderResponse = await fetch(orderUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderBody),
      });

      if (!orderResponse.ok) {
        throw new Error("Failed to create order");
      }

      const orderData = await orderResponse.json();

      // Prepare shipping address object (country hardcoded to India)
      const shippingAddress = {
        line1: addressLine1,
        line2: addressLine2,
        city: city,
        state: state,
        zipcode: zipcode,
        country: "India",
      };

      // Razorpay checkout options
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: isCampaign ? "Special Offer" : "Product Purchase",
        description: cartItems.map(item => `${item.productName} (x${item.quantity})`).join(", ").substring(0, 255),
        image: product.imageUrl || "",
        order_id: orderData.orderId,
        prefill: {
          name: customerName,
          email: customerEmail,
          contact: customerPhone,
        },
        notes: {
          shipping_address_line1: addressLine1,
          shipping_address_line2: addressLine2,
          shipping_address_city: city,
          shipping_address_state: state,
          shipping_address_zipcode: zipcode,
          shipping_address_country: country,
        },
        theme: {
          color: "#3399cc",
        },
        handler: async function (response) {
          // Payment successful - verify on backend
          console.log("Razorpay payment handler called:", response);
          console.log("Customer details from form state:", {
            customerName,
            customerEmail,
            customerPhone,
            shippingAddress,
          });

          // Ensure we have customer details before sending
          if (!customerName || !customerEmail || !customerPhone) {
            console.error(
              "Warning: Customer details are missing from form state!",
              {
                customerName: customerName || "MISSING",
                customerEmail: customerEmail || "MISSING",
                customerPhone: customerPhone || "MISSING",
              }
            );
          }

          try {
            const requestBody = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              customerDetails: {
                name: customerName || "",
                email: customerEmail || "",
                contact: customerPhone || "",
              },
              shippingAddress: shippingAddress || null,
              quantity: cartItems[0]?.quantity || 1,
              items: formattedItems
            };

            console.log(
              "Sending verifyPayment request with body:",
              JSON.stringify(requestBody, null, 2)
            );

            const verifyResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/checkout/verify-payment`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
              }
            );

            const verifyData = await verifyResponse.json();
            console.log("Verification response:", verifyData);

            if (verifyData.success) {
              // Show success message
              toast.success("Payment successful!");
              
              const successProps = {
                orderId: verifyData.orderId,
                paymentId: response.razorpay_payment_id,
                productId,
                amount: totalAmount,
                quantity: cartItems.reduce((sum, item) => sum + item.quantity, 0)
              };
              trackEvent("Payment Success", successProps);
              logger.info("Payment Success", successProps);

              setTimeout(() => {
                if (window.opener) {
                  window.close();
                } else {
                  window.location.replace(
                    `/payment-success?order_id=${verifyData.orderId}&payment_id=${response.razorpay_payment_id}`
                  );
                }
              }, 1500);
            } else {
              throw new Error(
                verifyData.error || "Payment verification failed"
              );
            }
          } catch (err) {
            console.error("Payment verification error:", err);
            toast.error("Payment verification failed. Please contact support.");
            
            const failProps = {
              error: err.message,
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id
            };
            trackEvent("Payment Verification Failed", failProps);
            logger.error("Payment Verification Failed", failProps);
            
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: function () {
            setProcessing(false);
            toast.info("Payment cancelled");
            
            trackEvent("Payment Modal Cancelled", { productId });
            logger.info("Payment Modal Cancelled", { productId });
          },
        },
      };
      
      trackEvent("Razorpay Modal Opened", { orderId: orderData.orderId });

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error("Payment error:", err);
      toast.error(err.message || "Failed to initiate payment");
      
      trackEvent("Payment Initiation Failed", { error: err.message });
      logger.error("Payment Initiation Failed", { error: err.message });
      
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading product details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigate("/")}
        >
          Go Home
        </Button>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Product not found</Alert>
      </Container>
    );
  }

  return (
    <>
      <Container 
        maxWidth="sm" 
        sx={{ 
          mt: 2, 
          mb: { xs: 10, sm: 12 },
          px: { xs: 2, sm: 3 },
        }}
      >
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{
          fontSize: { xs: "1.5rem", sm: "1.75rem" },
          fontWeight: 600,
        }}
      >
        Checkout
      </Typography>

      {isCampaign && product && (
        <Alert severity="info" className="mb-4 rounded-xl border-blue-100 bg-blue-50/50">
          <Typography variant="body2" className="font-semibold text-blue-800">
            ✨ Special offer from {product.influencerName}!
          </Typography>
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Order Summary - Collapsible */}
        <Grid item xs={12}>
          <Card className="card-modern">
            <CardContent sx={{ p: { xs: 2, sm: 3 }, pb: orderSummaryExpanded ? { xs: 1, sm: 1.5 } : { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => setOrderSummaryExpanded(!orderSummaryExpanded)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <ShoppingCart sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                  <Typography 
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.125rem" },
                      fontWeight: 600,
                    }}
                  >
                    Order Summary
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.875rem", sm: "0.9375rem" } }}
                  >
                    {cartItems.reduce((sum, item) => sum + item.quantity, 0)} {cartItems.reduce((sum, item) => sum + item.quantity, 0) === 1 ? "item" : "items"}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOrderSummaryExpanded(!orderSummaryExpanded);
                    }}
                    sx={{ p: 0.5 }}
                  >
                    {orderSummaryExpanded ? (
                      <ExpandLess sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                    ) : (
                      <ExpandMore sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                    )}
                  </IconButton>
                </Box>
              </Box>

              <Collapse in={orderSummaryExpanded}>
                <Box sx={{ mt: 2, mb: 0, pb: 0 }}>
                  {cartItems.map((item, idx) => {
                    const itemVariantGroups = getVariantGroups(item.variants);
                    return (
                      <Box key={item.productId} sx={{ mb: idx === cartItems.length - 1 ? 0 : 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "flex-start",
                          }}
                        >
                          {/* Product Thumbnail */}
                          {item.imageUrl && (
                            <Box
                              component="img"
                              src={item.imageUrl}
                              alt={item.productName}
                              sx={{
                                width: { xs: 80, sm: 100 },
                                height: { xs: 80, sm: 100 },
                                objectFit: "cover",
                                borderRadius: 1,
                                flexShrink: 0,
                              }}
                            />
                          )}

                          {/* Product Info */}
                          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                    fontWeight: 500,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {item.productName}
                                </Typography>
                              </Box>
                              {/* Price Section - Aligned to Right, same row as product name */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1, flexShrink: 0 }}>
                                {item.compareAtPrice && item.compareAtPrice > item.amount && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                      color: "text.secondary",
                                      textDecoration: "line-through",
                                    }}
                                  >
                                    ₹{((item.compareAtPrice * item.quantity) / 100).toFixed(2)}
                                  </Typography>
                                )}
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontSize: { xs: "1rem", sm: "1.125rem" },
                                    fontWeight: 600,
                                    color: "text.primary",
                                  }}
                                >
                                  ₹{((item.amount * item.quantity) / 100).toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Description and Quantity */}
                            <Box>
                              {item.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                    mb: 0.5,
                                  }}
                                >
                                  {item.description}
                                </Typography>
                              )}
                              
                              {/* Variants Selection */}
                              {Object.keys(itemVariantGroups).length > 0 && (
                                <Box sx={{ mt: 1.5, mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                  {Object.entries(itemVariantGroups).map(([optionName, values]) => (
                                    <FormControl size="small" key={optionName} sx={{ minWidth: 100 }}>
                                      <InputLabel>{optionName}</InputLabel>
                                      <Select
                                        value={item.selectedVariants[optionName] || ""}
                                        label={optionName}
                                        onChange={(e) => {
                                          const newVal = e.target.value;
                                          trackEvent("Variant Selected", { option: optionName, value: newVal });
                                          handleUpdateVariant(item.productId, optionName, newVal);
                                        }}
                                        sx={{ borderRadius: '0.5rem' }}
                                      >
                                        {values.map(val => (
                                          <MenuItem key={val} value={val}>{val}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  ))}
                                </Box>
                              )}

                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newQty = item.quantity - 1;
                                    trackEvent("Quantity Decremented", { from: item.quantity, to: newQty });
                                    handleUpdateQuantity(item.productId, newQty);
                                  }}
                                  sx={{
                                    p: 0.5,
                                    minWidth: "auto",
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                  }}
                                >
                                  <Remove sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }} />
                                </IconButton>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                    fontWeight: 500,
                                    minWidth: 20,
                                    textAlign: "center",
                                  }}
                                >
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newQty = item.quantity + 1;
                                    trackEvent("Quantity Incremented", { from: item.quantity, to: newQty });
                                    handleUpdateQuantity(item.productId, newQty);
                                  }}
                                  sx={{
                                    p: 0.5,
                                    minWidth: "auto",
                                    width: { xs: 28, sm: 32 },
                                    height: { xs: 28, sm: 32 },
                                  }}
                                >
                                  <Add sx={{ fontSize: { xs: "1rem", sm: "1.125rem" } }} />
                                </IconButton>
                                {item.productId !== productId && (
                                  <Button 
                                    size="small" 
                                    color="error" 
                                    onClick={() => handleUpdateQuantity(item.productId, 0)}
                                    sx={{ ml: 'auto', fontSize: '0.75rem', textTransform: 'none' }}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </Box>

                            </Box>
                          </Box>
                        </Box>
                        {idx < cartItems.length - 1 && <Divider sx={{ my: 2 }} />}
                      </Box>
                    );
                  })}

                  {/* Total */}
                  <Box
                    sx={{
                      mt: 1,
                      pt: 2,
                      pb: 0,
                      mb: 0,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.125rem" },
                        fontWeight: 600,
                      }}
                    >
                      Total
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1.125rem", sm: "1.25rem" },
                        fontWeight: 700,
                      }}
                    >
                      ₹{(totalAmount / 100).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        {/* Checkout Upsell Products */}
        {upsells && upsells.length > 0 && (
          <Grid item xs={12}>
            <Card className="card-modern">
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontSize: { xs: "1rem", sm: "1.125rem" }, 
                    fontWeight: 600,
                    mb: 2 
                  }}
                >
                  Complete your order
                </Typography>
                <Box 
                  sx={{ 
                    maxHeight: 280, 
                    overflowY: "auto", 
                    pr: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#c1c1c1",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#a8a8a8",
                    },
                  }}
                >
                  {upsells.map((upsell) => {
                    const alreadyAdded = cartItems.some(item => item.productId === `prod_${upsell._id}`);
                    return (
                      <Box 
                        key={upsell._id}
                        sx={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: 2,
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "background.paper",
                          transition: "transform 0.2s, box-shadow 0.2s",
                          "&:hover": {
                            boxShadow: 1,
                          }
                        }}
                      >
                        {upsell.thumbnailUrl && (
                          <Box
                            component="img"
                            src={upsell.thumbnailUrl}
                            alt={upsell.title}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500, 
                              overflow: "hidden", 
                              textOverflow: "ellipsis", 
                              whiteSpace: "nowrap" 
                            }}
                          >
                            {upsell.title}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ fontWeight: 600, mt: 0.5 }}
                          >
                            ₹{upsell.price.toFixed(2)}
                          </Typography>
                        </Box>
                        <Button
                          variant={alreadyAdded ? "outlined" : "contained"}
                          size="small"
                          disabled={alreadyAdded}
                          onClick={() => handleAddUpsell(upsell)}
                          sx={{ 
                            borderRadius: 2, 
                            textTransform: "none",
                            minWidth: 70,
                            fontWeight: 600
                          }}
                        >
                          {alreadyAdded ? "Added" : "Add"}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Recipient Details - Collapsible */}
        <Grid item xs={12}>
          <Card className="card-modern">
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => setRecipientExpanded(!recipientExpanded)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Person sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                  <Typography 
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.125rem" },
                      fontWeight: 600,
                    }}
                  >
                    Recipient
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRecipientExpanded(!recipientExpanded);
                  }}
                  sx={{ p: 0.5 }}
                >
                  {recipientExpanded ? (
                    <ExpandLess sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                  )}
                </IconButton>
              </Box>

              <Collapse in={recipientExpanded}>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        onFocus={() => {
                          if (!hasStartedRecipient) {
                            setHasStartedRecipient(true);
                            trackEvent("Recipient Info Started", { productId });
                            logger.info("Recipient Info Started", { productId });
                          }
                        }}
                        required

                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        required
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        {/* Delivery Address - Collapsible */}
        <Grid item xs={12}>
          <Card className="card-modern">
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => setDeliveryAddressExpanded(!deliveryAddressExpanded)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocationOn sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                  <Typography 
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.125rem" },
                      fontWeight: 600,
                    }}
                  >
                    Delivery Address
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeliveryAddressExpanded(!deliveryAddressExpanded);
                  }}
                  sx={{ p: 0.5 }}
                >
                  {deliveryAddressExpanded ? (
                    <ExpandLess sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
                  )}
                </IconButton>
              </Box>

              <Collapse in={deliveryAddressExpanded}>
                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="House No, Building"
                        value={addressLine1}
                        onChange={(e) => setAddressLine1(e.target.value)}
                        onFocus={() => {
                          if (!hasStartedAddress) {
                            setHasStartedAddress(true);
                            trackEvent("Address Info Started", { productId });
                            logger.info("Address Info Started", { productId });
                          }
                        }}
                        required

                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Area, Locality"
                        value={addressLine2}
                        onChange={(e) => setAddressLine2(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="PIN Code"
                        value={zipcode}
                        onChange={(e) => setZipcode(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="State"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        required
                      />
                    </Grid>
                    {/* Country field commented out - hardcoded to India */}
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Country *"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        required
                      />
                    </Grid> */}
                  </Grid>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Container>

      {/* Sticky Pay Button */}
      <Box
        component="form"
        onSubmit={handlePayment}
        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 sm:p-6 z-[1000] shadow-lg"
      >
        <Container maxWidth="sm" className="px-4 sm:px-6">
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={processing}
            fullWidth
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case disabled:opacity-50 text-base sm:text-lg"
          >
            {processing ? (
              <CircularProgress size={24} className="text-white" />
            ) : (
              "Proceed to Pay"
            )}
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default CheckoutPage;
